import { DateTime } from "luxon";
import {
  ComponentPropsWithoutRef,
  forwardRef,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Grid } from "../Grid";
import { SelectCard } from "../SelectCard";
import { useCalendar } from "./CalendarContext";
import styles from "./Calendar.module.scss";
import cx from "classnames";
import { useMergeRefs } from "../../hooks";

export type CalendarYearSelectionProps = ComponentPropsWithoutRef<"div"> & {
  /**
   * The function to call when a year is selected.
   *
   * @param year - The year that is selected.
   */
  onYearSelection: (year: DateTime["year"]) => void;
};

/**
 * A component that displays a grid of year cards and allows the user to select a year.
 *
 * @param props.onYearSelection - The function to call when a year is selected.
 * @param props - The rest of the props for the div wrapper.
 * @returns A component that displays a grid of year cards and allows the user to select a year.
 */
export const CalendarYearSelection = forwardRef<
  HTMLDivElement,
  CalendarYearSelectionProps
>(({ onYearSelection, ...props }, ref) => {
  const { maxDate, minDate, selectedYear } = useCalendar();

  const containerRef = useRef<HTMLDivElement>(null);

  const combinedRef = useMergeRefs([ref, containerRef]);

  const initialYears = useMemo(
    () =>
      Array.from({ length: 52 }, (_, i) => {
        const year = selectedYear - (selectedYear % 2 ? 24 : 25) + i;

        // disable the select card if outside the min or max date
        const disabled =
          (minDate && year < minDate.year) || (maxDate && year > maxDate.year);

        return { year, disabled: disabled ?? false };
      }),
    [selectedYear, minDate, maxDate],
  );

  const [dates, setDates] = useState<
    {
      year: DateTime["year"];
      disabled: boolean;
    }[]
  >(initialYears);

  useLayoutEffect(() => {
    // scroll to the middle of the container on mount
    containerRef.current?.scrollTo({
      top:
        (containerRef.current?.scrollHeight ?? 0) / 2 -
        (containerRef.current?.clientHeight ?? 0) / 2,
    });

    // add scroll listener to load more years as the user scrolls
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromTop = scrollTop;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      if (distanceFromTop < 200 && !minDate) {
        // Load 20 more years at the beginning unless minDate is set
        setDates((prevDates) => {
          const firstYear = prevDates[0].year;
          const newDates = Array.from({ length: 20 }, (_, i) => ({
            year: firstYear - (20 - i),
            disabled: false,
          }));
          return [...newDates, ...prevDates];
        });
      } else if (distanceFromBottom < 200 && !maxDate) {
        // Load 20 more years at the end unless maxDate is set
        setDates((prevDates) => {
          const lastYear = prevDates[prevDates.length - 1].year;
          const newDates = Array.from({ length: 20 }, (_, i) => ({
            year: lastYear + (i + 1),
            disabled: false,
          }));
          return [...prevDates, ...newDates];
        });
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [minDate, maxDate]);

  const classes = cx(styles["year-selection"], props.className);

  const [focusedIndex, setFocusedIndex] = useState(
    // start focused on the middle of the year selection, with odd numbers on the left
    selectedYear % 2 ? 24 : 25,
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const cards = containerRef.current?.querySelectorAll(
        "input[type='checkbox']",
      );
      if (!cards || cards.length === 0) {
        return;
      }

      const cardArray = Array.from(cards);
      const currentIndex = cardArray.indexOf(target as HTMLButtonElement);

      if (currentIndex === -1) return;

      const updateFocusedIndex = (index: number) => {
        e.preventDefault();
        (cardArray[index] as HTMLButtonElement).focus();
        setFocusedIndex(index);
      };

      switch (e.key) {
        case "ArrowLeft":
          updateFocusedIndex(currentIndex - 1);
          break;

        case "ArrowRight":
          updateFocusedIndex(currentIndex + 1);
          break;

        case "ArrowUp":
          updateFocusedIndex(currentIndex - 2);
          break;

        case "ArrowDown":
          updateFocusedIndex(currentIndex + 2);
          break;

        case "Enter":
        case " ":
          e.preventDefault();
          onYearSelection(dates[currentIndex].year);
          break;
      }
    },
    [dates, onYearSelection],
  );

  return (
    <div
      ref={combinedRef}
      data-anv="calendar-year-selection"
      className={classes}
      {...props}
    >
      <div role="grid" aria-label="Calendar year selection">
        <Grid
          templateColumns="repeat(2, minmax(0, 1fr))"
          gap={3}
          onKeyDown={handleKeyDown}
          role="row"
        >
          {dates.map(({ year, disabled }, index) => {
            const yearDate = DateTime.fromObject({ year }).toFormat("yyyy");
            return (
              <div role="gridcell" key={yearDate}>
                <SelectCard
                  id={yearDate}
                  onChange={() => onYearSelection(year)}
                  checked={selectedYear === year}
                  checkboxProps={{
                    className: styles["selection-checkbox"],
                    tabIndex: focusedIndex === index ? 0 : -1,
                  }}
                  className={styles["selection-card"]}
                  disabled={disabled}
                >
                  {yearDate}
                </SelectCard>
              </div>
            );
          })}
        </Grid>
      </div>
    </div>
  );
});

CalendarYearSelection.displayName = "CalendarYearSelection";
